import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import HeroSection from '../components/sections/HeroSection';

const TermsPage: React.FC = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Terms & Conditions | Consiliuma Web Design & Development</title>
        <meta name="description" content="Our terms and conditions for web design and development services. Clear guidelines for project management, communication, and payment terms." />
        <meta name="keywords" content="Terms and Conditions, Web Design Terms, Development Agreement, Project Terms" />
        <link rel="canonical" href="/terms-conditions" />
      </Helmet>

      <HeroSection
        title="Terms & Conditions"
        subtitle="Clear guidelines for successful project delivery"
        ctaText="Get Started"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/5473298/pexels-photo-5473298.jpeg"
      />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                Last updated: {new Date().toLocaleDateString('en-GB', { 
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">1. Agreement to Terms</h2>
                  <p>By engaging our services, you agree to be bound by these Terms and Conditions. Please read them carefully before proceeding with any project.</p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">2. Project Communication</h2>
                  <h3 className="text-xl font-bold mb-3">2.1 Single Point of Contact</h3>
                  <p>Each project must have a designated single point of contact from the client's side. This requirement is crucial for:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>Maintaining clear and consistent communication</li>
                    <li>Avoiding conflicting feedback and requirements</li>
                    <li>Ensuring efficient decision-making processes</li>
                    <li>Preventing project delays due to multiple, potentially conflicting opinions</li>
                    <li>Maintaining project momentum and focus</li>
                  </ul>
                  <p>Multiple stakeholders often lead to:</p>
                  <ul className="list-disc ml-6">
                    <li>Increased project complexity and confusion</li>
                    <li>Extended timelines due to varied feedback</li>
                    <li>Higher costs from managing multiple communication channels</li>
                    <li>Potential scope creep from differing requirements</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">3. Payment Terms</h2>
                  <h3 className="text-xl font-bold mb-3">3.1 Payment Structure</h3>
                  <p>For projects below £2,500:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>50% initial payment required to commence work</li>
                    <li>25% payment at project midpoint</li>
                    <li>25% final payment upon completion</li>
                  </ul>

                  <h3 className="text-xl font-bold mb-3">3.2 Upfront Payment Discount</h3>
                  <p>For projects over £1,500:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>10% discount applied when paid in full upfront</li>
                    <li>Typically results in faster project completion</li>
                    <li>Helps maintain optimal project workflow</li>
                    <li>Ensures immediate resource allocation</li>
                  </ul>

                  <h3 className="text-xl font-bold mb-3">3.3 Payment Methods</h3>
                  <p>We accept payments via:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>HSBC UK (GBP)</li>
                    <li>USDT</li>
                  </ul>

                  <h3 className="text-xl font-bold mb-3">3.4 Late Payments</h3>
                  <p>Important considerations regarding late payments:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>Project work will pause immediately if payments are not received on time</li>
                    <li>7 days notice required for any payment delays</li>
                    <li>Immediate restart of work cannot be guaranteed after late payment</li>
                    <li>Project timelines will be adjusted based on payment delays</li>
                    <li>Existing commitments to other clients may affect project resumption</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">4. Project Changes and Additional Costs</h2>
                  <h3 className="text-xl font-bold mb-3">4.1 Change Requests</h3>
                  <p>Project modifications and their implications:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>Small, incremental changes may be accommodated without additional charges</li>
                    <li>Significant changes requiring substantial additional work will incur extra costs</li>
                    <li>All major changes must be agreed upon in writing</li>
                    <li>Change requests may affect project timeline</li>
                  </ul>

                  <h3 className="text-xl font-bold mb-3">4.2 Client Responsibilities</h3>
                  <p>The client is responsible for:</p>
                  <ul className="list-disc ml-6 mb-4">
                    <li>All subscription costs (e.g., hosting, premium plugins, third-party services)</li>
                    <li>Domain registration and renewal fees</li>
                    <li>SSL certificate costs (if not included in hosting)</li>
                    <li>Any premium services or add-ons required for the project</li>
                    <li>Ongoing maintenance costs (unless covered by a maintenance package)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">5. Project Timeline and Delivery</h2>
                  <p>Project timelines are subject to:</p>
                  <ul className="list-disc ml-6">
                    <li>Timely provision of required materials and feedback</li>
                    <li>Adherence to payment schedule</li>
                    <li>Clear and prompt communication</li>
                    <li>Agreed-upon scope limitations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">6. Intellectual Property</h2>
                  <p>Ownership of work:</p>
                  <ul className="list-disc ml-6">
                    <li>Rights transfer upon final payment</li>
                    <li>Development work remains our property until paid in full</li>
                    <li>Third-party assets subject to respective licenses</li>
                    <li>Portfolio rights retained for promotional purposes</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">7. Project Requirements</h2>
                  <p>Client responsibilities include:</p>
                  <ul className="list-disc ml-6">
                    <li>Providing necessary content and materials</li>
                    <li>Timely feedback and approvals</li>
                    <li>Maintaining single point of contact</li>
                    <li>Meeting payment obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">8. Project Cancellation</h2>
                  <p>Cancellation terms:</p>
                  <ul className="list-disc ml-6">
                    <li>Written notice required</li>
                    <li>Payment for completed work</li>
                    <li>No refund of initial deposit</li>
                    <li>Intellectual property retention until final payment</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">9. Warranty and Support</h2>
                  <p>Post-launch support:</p>
                  <ul className="list-disc ml-6">
                    <li>30-day bug fix warranty</li>
                    <li>Maintenance packages available</li>
                    <li>Regular updates and security patches</li>
                    <li>Technical support options</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-primary mb-4">10. Limitation of Liability</h2>
                  <p>Our liability is limited to:</p>
                  <ul className="list-disc ml-6">
                    <li>Project value paid</li>
                    <li>Direct damages only</li>
                    <li>Excludes consequential losses</li>
                    <li>Force majeure events</li>
                  </ul>
                </section>

                <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Note: These terms and conditions are governed by English law. By proceeding with our services, you agree to these terms in their entirety.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default TermsPage;